import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useDayUi } from '../features/day/dayUiStore';
import { useMonthUi } from '../features/month/monthUiStore';
import { routeViewOf, useToday, ViewKind } from '../hooks/useRouteView';
import { shortcutNavigation } from './globalShortcutTarget';
import { resolveGlobalShortcut } from './shortcuts';
import { useViewUi } from './viewUiStore';

/** Selector of the elements that make the page modal or popover-driven: dialogs and sheets. */
const OVERLAY_SELECTOR = 'dialog[open], [role="dialog"], [aria-modal="true"], [role="menu"]';

/**
 * True while something owns the keyboard: a picker, context menu, overlap popover, the help
 * overlay or any dialog is open, or a drag is in progress (2026-10b §10.2).
 */
export function globalShortcutsBlocked(): boolean {
  const ui = useDayUi.getState();
  if (
    ui.picker !== null ||
    ui.contextMenu !== null ||
    ui.conflict !== null ||
    ui.helpOpen ||
    ui.dragSpan !== null
  ) {
    return true;
  }
  return document.querySelector(OVERLAY_SELECTOR) !== null;
}

/** The hash route's path (`/day/2026-10-07`), without a query. */
function currentPath(): string {
  return window.location.hash.replace(/^#/, '').split('?')[0] ?? '';
}

/**
 * Global date and view shortcuts on the day, week, month and statistics pages: `[` `]` step the
 * period, T goes to today, D / W / M switch the view (Ctrl+Alt+1 / 3 / 4 likewise). Matches
 * `KeyboardEvent.code`, so the typed character, the keyboard layout and the input method's mode
 * do not matter. `?` opens the help overlay on the month view (day and week views handle it).
 */
export function useGlobalShortcuts(): void {
  const navigate = useNavigate();
  const today = useToday();
  const latestToday = useRef(today);
  useEffect(() => {
    latestToday.current = today;
  });

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.defaultPrevented) return;
      // The route is read from the address at press time, so a key pressed right after a route
      // change never acts on the previous view.
      const current = routeViewOf(currentPath());
      const now = latestToday.current;
      if (
        current.kind === ViewKind.Month &&
        (event.key === '?' || event.key === '？') &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey
      ) {
        const target = event.target as HTMLElement | null;
        if (target?.closest('input, textarea, select, [contenteditable]') != null) return;
        event.preventDefault();
        useDayUi.getState().toggleHelp();
        return;
      }
      const shortcut = resolveGlobalShortcut(event);
      if (shortcut === null || globalShortcutsBlocked()) return;
      const target = shortcutNavigation(
        shortcut,
        current,
        now,
        useMonthUi.getState().focusedDate,
        useViewUi.getState().lastDayDate,
      );
      if (target === null) return;
      event.preventDefault();
      if (target.focusMonthDate !== undefined) useMonthUi.getState().requestFocus(target.focusMonthDate, true);
      void navigate(target.path);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate]);
}
